import React, {HTMLAttributes, useEffect, useState} from 'react';
import Link from 'next/link';

import {ICategoryDictionary} from '@/dictionaries/pages/category/types';
import {IoCloseSharp} from 'react-icons/io5';

import MultiRangeSlider from '@/components/MultiRangeSlider';
import Button from '@/components/Button';
import Select from '@/components/Select';

import {languages} from '../ExperienceType/mock';

import styles from './styles.module.css';

interface ICategoryFilterProps extends HTMLAttributes<HTMLDivElement> {
  lang: string;
  dict: ICategoryDictionary;
  category: string;
  openFilter: boolean;
  setOpenFilter: (open: boolean) => void;
  typesIds: string[];
  allTypesIds: string[];
  setChosenTypes: (types: string[]) => void;
  chosenLanguages: string[];
  setChosenLanguages: (languages: string[]) => void;
  chosenZone: {label: string; value: string};
  setChosenZone: (zone: {label: string; value: string}) => void;
  chosenPrice: {minPrice: number; maxPrice: number};
  setChosenPrice: (price: {minPrice: number; maxPrice: number}) => void;
}

export const CategoryFilter: React.FC<ICategoryFilterProps> = ({
  lang,
  dict,
  category,
  openFilter,
  setOpenFilter,
  typesIds,
  allTypesIds,
  setChosenTypes,
  chosenLanguages,
  setChosenLanguages,
  chosenZone,
  setChosenZone,
  chosenPrice,
  setChosenPrice,
}) => {
  const [selectedLanguages, setSelectedLanguages] =
    useState<string[]>(chosenLanguages);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(typesIds);
  const [selectedZone, setSelectedZone] = useState(chosenZone);
  const [sliderMin, setSliderMin] = useState(0);
  const [sliderMax, setSliderMax] = useState(3000);
  const [isAnimating, setIsAnimating] = useState(false);
  const [resetRange, setResetRange] = useState(false);

  const handleTransitionEnd = () => {
    if (!openFilter) {
      setIsAnimating(false);
    }
  };

  const handleLanguageClick = (language: string) => {
    if (selectedLanguages.includes(language)) {
      setSelectedLanguages(
        selectedLanguages.filter((lang) => lang !== language),
      );
    } else {
      setSelectedLanguages([...selectedLanguages, language]);
    }
  };

  const handleTypeClick = (type: string) => {
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter((item) => item !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };

  const categoriesList = dict?.experienceCategory.map((item, index) => {
    return (
      <div
        key={index}
        className={`py-[20px] px-[40px] border-b border-r border-gray-200 text-lg hover:text-blue font-averta-semibold hover:cursor-pointer ${
          item.title === category && 'text-blue border-r-0'
        }`}>
        <Link
          onClick={() => {
            document.documentElement.classList.remove('overflow-y-hidden');
          }}
          href={lang === 'en' ? item.href : (item.filterHref as string)}>
          {item.title}
        </Link>
      </div>
    );
  });

  const languageButtons = languages.map((language, index) => {
    const isSelected = selectedLanguages.includes(language);
    return (
      <button
        key={index}
        className={`w-11 h-[44px] border border-gray-200 text-sm uppercase ${
          isSelected ? 'bg-blue text-white' : 'bg-gray-100 text-gray-700'
        }`}
        onClick={() => handleLanguageClick(language)}>
        {language}
      </button>
    );
  });

  const matchedCategory = dict.experienceCategory.find(
    (cat) => cat.title === category,
  );

  const types = (matchedCategory && matchedCategory.type) || [];

  const typeButtons = types.map((type, index) => {
    const isSelected = selectedTypes.includes(type.id);
    return (
      <div key={index} className={`text-sm`}>
        <label
          className="flex mr-auto items-center cursor-pointer"
          onClick={() => handleTypeClick(type.id)}>
          <span
            className={`w-[25px] h-[25px] border border-gray-200 rounded-full mr-[10px] ${
              isSelected ? 'bg-blue' : 'bg-gray-100 '
            }`}></span>
          <span className={`${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
            {type.title}
          </span>
        </label>
      </div>
    );
  });

  const applyFilter = () => {
    setChosenTypes(selectedTypes);
    setChosenLanguages(selectedLanguages);
    setChosenZone(selectedZone);
    setChosenPrice({minPrice: sliderMin, maxPrice: sliderMax});
    setOpenFilter(false);
    document.documentElement.classList.remove('overflow-y-hidden');
  };

  const resetFilter = () => {
    setSelectedTypes(allTypesIds);
    setSelectedLanguages([]);
    setSelectedZone({
      label: `${dict.filter.zoneOptions[0].label}`,
      value: 'any',
    });
    setSliderMin(0);
    setSliderMax(3000);
    setChosenPrice({minPrice: 0, maxPrice: 3000});
    setResetRange(true);
  };

  useEffect(() => {
    if (openFilter && isAnimating) {
      const animationDuration = 100;
      const timeout = setTimeout(() => {
        setIsAnimating(false);
        setOpenFilter(false);
        document.documentElement.classList.remove('overflow-y-hidden');
      }, animationDuration);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line
  }, [openFilter, isAnimating]);

  useEffect(() => {
    if (openFilter) {
      document.documentElement.classList.add('overflow-y-hidden');
    }
  }, [openFilter]);

  return (
    <div
      className={`flex overflow-hidden transition-opacity duration-300 fixed z-[999] top-0 left-0 w-full h-full bg-white bg-opacity-90 overlay`}>
      {openFilter && (
        <div
          id="filter"
          className={`flex flex-col desktop:flex-row desktop:w-[60vw] overflow-y-scroll w-full float-none p-0 text-gray-700 bg-white h-full shadow-default ${
            openFilter && styles['animate-slide-in']
          } ${isAnimating && styles['animate-slide-out']}`}
          onTransitionEnd={handleTransitionEnd}>
          <div className="flex flex-col w-full desktop:w-1/2">
            <div className="pt-[30px] px-[40px] pb-[10px] border-b border-r border-gray-200 text-sm">
              {dict.filter.category}
            </div>
            <div className="p-0 overflow-auto border-r-0">{categoriesList}</div>
            <div className="flex-1 border-r border-gray-200"></div>
          </div>

          <div className="desktop:relative flex flex-col justify-between px-[40px] py-[30px] h-full w-full desktop:w-1/2">
            <div
              className="absolute top-[10px] right-[15px] cursor-pointer"
              onClick={() => setIsAnimating(true)}>
              <IoCloseSharp className="w-8 h-8 text-gray-500" />
            </div>

            <div>
              {types.length > 1 && (
                <div className="flex flex-col items-start mb-8">
                  <label className="text-sm mb-4">{dict.filter.type}</label>
                  <div className="flex flex-col gap-4">
                    {typeButtons}
                    <div className="inline-block">
                      <button
                        className="uppercase cursor-pointer text-blue"
                        onClick={() => setSelectedTypes(allTypesIds)}>
                        {dict.filter.selectAllButton}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col mb-8 text-gray-900">
                <Select
                  options={dict.filter.zoneOptions}
                  label={dict.filter.zone}
                  id={'zone-selector'}
                  onChange={({label, value}) => {
                    setSelectedZone({label, value});
                  }}
                  selectedValue={dict.filter.zoneOptions.find(
                    (option) => option.label === selectedZone.label,
                  )}
                />
              </div>
              <div className="flex flex-col mb-8">
                <label className="text-sm mb-4">{dict.filter.languages}</label>
                <div>{languageButtons}</div>
              </div>

              <div className="flex flex-col w-full">
                <div className="flex justify-between">
                  <label className="text-sm text-gray-700 leading-[1.8] w-fit">
                    {dict.filter.price}
                  </label>
                  <p className="text-md text-black">{`$ ${sliderMin.toLocaleString(
                    'en-US',
                  )} — $ ${sliderMax.toLocaleString('en-US')}`}</p>
                </div>
                <MultiRangeSlider
                  min={0}
                  max={3000}
                  currentMin={chosenPrice.minPrice}
                  currentMax={chosenPrice.maxPrice}
                  onChange={({min, max}) => {
                    setSliderMin(min);
                    setSliderMax(max);
                  }}
                  reset={resetRange}
                  setReset={setResetRange}
                />
              </div>
            </div>
            <div className="flex flex-row justify-center items-center gap-8 py-8 h-auto w-full">
              <Button
                variant="text"
                className="w-[20%]"
                onClick={() => resetFilter()}>
                {dict.filter.resetButton}
              </Button>
              <Button
                className="hidden tablet:block w-auto"
                onClick={() => applyFilter()}>
                {dict.filter.applyButton}
              </Button>
              <Button
                className="block tablet:hidden w-auto"
                onClick={() => applyFilter()}>
                {dict.filter.applyButtonShort}
              </Button>
            </div>
          </div>
        </div>
      )}
      {openFilter && !isAnimating && (
        <div
          className="hidden desktop:flex desktop:w-[40vw]"
          onClick={() => setIsAnimating(true)}></div>
      )}
    </div>
  );
};
